import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image'
import React, { FC } from 'react'
import BertholdTitle from 'src/components/atoms/bertholdTitle'
import Eyebrow from 'src/images/svgs/Eyebrow.svg'
import ChevronRight from 'src/images/svgs/icons/ChevronRight.svg'
import { PrismicLinkType } from 'src/typings/generated/graphql'
import CustomLink from 'src/utils/customLink'
import * as Styles from './menuCarouselCard.module.scss'

/**
 * Props for the `MenuCarouselCard` component.
 */
export type MenuCarouselCardProps = {
  /**
   * The name of the menu item to be displayed on the card. Used as the main headline of the card.
   */
  name?: string
  /**
   * The image of the menu item to be displayed on the card.
   */
  image: IGatsbyImageData
  /**
   * The alternative text for the image of the menu item.
   */
  imageAlt?: string
  /**
   * The description of the menu item to be displayed on the card. Usually a short paragraph describing the item's
   * ingredients, serving size, or other relevant information.
   */
  description?: string
  /**
   * Text representing the number of calories in the menu item.
   */
  calories?: string
  /**
   * If provided, the card will act as a link to the specified URL.
   */
  link?: PrismicLinkType
  /**
   * If provided, this text will be displayed as a call-to-action on the card, prompting users to click for more
   * information or to take some action.
   */
  ctaText?: string
  /**
   * If true, the card will expand to show the menu item description and calories when hovered over. The card will
   * always appears expanded otherwise.
   */
  expandOnHover?: boolean
}

/**
 * A card component that displays information about a menu item, used in the `MenuCarousel` component.
 */
export const MenuCarouselCard: FC<MenuCarouselCardProps> = ({
  name,
  image,
  imageAlt,
  description,
  calories,
  link,
  ctaText,
  expandOnHover = false,
}) => {
  const shouldDisplayName = !!name
  const shouldDisplayImage = !!image
  const shouldDisplayDescription = !!description
  const shouldDisplayCalories = !!calories
  const shouldDisplayCta = !!ctaText

  const shouldDisplayDescriptionSeparator =
    shouldDisplayDescription && shouldDisplayCalories

  return (
    <article
      className={`${Styles.menuCarouselCardWrapper} ${
        expandOnHover ? Styles.expandOnHover : ''
      }`}
    >
      <CustomLink link={link} className={Styles.menuCarouselCard}>
        <div className={Styles.titleSection}>
          {shouldDisplayName && (
            <>
              <Eyebrow className={Styles.eyebrow} aria-hidden="true" />
              <h3 className={Styles.title}>{BertholdTitle({ text: name })}</h3>
            </>
          )}
        </div>

        <div className={Styles.imageSection}>
          {shouldDisplayImage && (
            <GatsbyImage
              image={image}
              alt={imageAlt ?? ''}
              className={Styles.image}
            />
          )}
        </div>

        <div className={Styles.descriptionSection}>
          {shouldDisplayDescription && (
            <p className={Styles.description}>{description}</p>
          )}
          {shouldDisplayDescriptionSeparator && (
            <span className={Styles.descriptionSeparator} aria-hidden="true">
              -
            </span>
          )}
          {shouldDisplayCalories && (
            <p className={Styles.calories}>{calories}</p>
          )}
        </div>

        <div className={Styles.ctaSection}>
          {shouldDisplayCta && (
            <>
              <div className={Styles.ctaText}>{ctaText}</div>
              <ChevronRight className={Styles.ctaIcon} aria-hidden="true" />
            </>
          )}
        </div>
      </CustomLink>
    </article>
  )
}
